import { Accordion, AccordionSummary, Typography, AccordionDetails, Box } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { RiArrowDownSFill } from 'react-icons/ri';
import { FormattedMessage, useIntl } from 'react-intl';

import { InlineBadge } from 'components/UI';
import { InlineSelect, AmountTextField } from 'components/forms';

import { useCommonFiltersStyles } from '../useCommonFiltersStyles';
import { useProductTypeOptions } from '../options';
import { primaryDealFiltersHelper } from './helpers';
import { CommonPrimaryDealFilterValues } from './types';

interface Props {
  accordionExpanded?: boolean;
}

export const CommonFilters = ({ accordionExpanded }: Props) => {
  const classes = useCommonFiltersStyles();
  const intl = useIntl();

  const formik = useFormikContext<CommonPrimaryDealFilterValues>();

  const productTypeOptions = useProductTypeOptions(intl);

  const amountCount = primaryDealFiltersHelper.countAmountValues(formik.values);
  const durationCount = primaryDealFiltersHelper.countDurationValues(formik.values);
  const nominalInterestRateCount = primaryDealFiltersHelper.countNominalInterestRateValues(formik.values);
  const providerCompositeScoreCount = primaryDealFiltersHelper.countProviderCompositeScoreValues(formik.values);
  const defaultRateCount = primaryDealFiltersHelper.countDefaultRateValues(formik.values);
  const openAmountCount = primaryDealFiltersHelper.countOpenAmountValues(formik.values);

  return (
    <>
      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="borrower.type" />
          </Typography>

          {formik.values.productType.length > 0 && (
            <InlineBadge label={formik.values.productType.length} />
          )}
        </AccordionSummary>

        <AccordionDetails>
          <Field
            id="productType"
            name="productType"
            component={InlineSelect}
            options={productTypeOptions}
            multiple
            className={classes.inlineSelect}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="loan_amount" />
          </Typography>

          {amountCount > 0 && <InlineBadge label={amountCount} />}
        </AccordionSummary>

        <AccordionDetails>
          <Box className={classes.rangeInput}>
            <Field
              id="minPrincipal"
              name="minPrincipal"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'min_amount' })}
              size="medium"
            />

            <Box className={classes.dash} />

            <Field
              id="maxPrincipal"
              name="maxPrincipal"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'max_amount' })}
              size="medium"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Rating filter intentionally hidden for markets per product request. */}

      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="loan_duration.in_months" />
          </Typography>

          {durationCount > 0 && <InlineBadge label={durationCount} />}
        </AccordionSummary>

        <AccordionDetails>
          <Box className={classes.rangeInput}>
            <Field
              id="minDuration"
              name="minDuration"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'min_duration' })}
              size="medium"
              inputProps={{
                thousandSeparator: false,
                decimalScale: 0,
              }}
            />
            <Box className={classes.dash} />

            <Field
              id="maxDuration"
              name="maxDuration"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'max_duration' })}
              size="medium"
              inputProps={{
                thousandSeparator: false,
                decimalScale: 0,
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="interest_rate.nominal.short" />
          </Typography>

          {nominalInterestRateCount > 0 && <InlineBadge label={nominalInterestRateCount} />}
        </AccordionSummary>

        <AccordionDetails>
          <Box className={classes.rangeInput}>
            <Field
              id="minNominalInterestRate"
              name="minNominalInterestRate"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'min_amount' })}
              size="medium"
              inputProps={{ thousandSeparator: false, decimalScale: 2 }}
            />

            <Box className={classes.dash} />

            <Field
              id="maxNominalInterestRate"
              name="maxNominalInterestRate"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'max_amount' })}
              size="medium"
              inputProps={{ thousandSeparator: false, decimalScale: 2 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="rating.provider" />
          </Typography>

          {providerCompositeScoreCount > 0 && <InlineBadge label={providerCompositeScoreCount} />}
        </AccordionSummary>

        <AccordionDetails>
          <Box className={classes.rangeInput}>
            <Field
              id="minProviderCompositeScore"
              name="minProviderCompositeScore"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'min_amount' })}
              size="medium"
              inputProps={{ thousandSeparator: false, decimalScale: 2 }}
            />

            <Box className={classes.dash} />

            <Field
              id="maxProviderCompositeScore"
              name="maxProviderCompositeScore"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'max_amount' })}
              size="medium"
              inputProps={{ thousandSeparator: false, decimalScale: 2 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="expected_default_rate.short" />
          </Typography>

          {defaultRateCount > 0 && <InlineBadge label={defaultRateCount} />}
        </AccordionSummary>

        <AccordionDetails>
          <Box className={classes.rangeInput}>
            <Field
              id="minDefaultRate"
              name="minDefaultRate"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'min_amount' })}
              size="medium"
              inputProps={{ thousandSeparator: false, decimalScale: 3 }}
            />

            <Box className={classes.dash} />

            <Field
              id="maxDefaultRate"
              name="maxDefaultRate"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'max_amount' })}
              size="medium"
              inputProps={{ thousandSeparator: false, decimalScale: 3 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.accordion} elevation={0} defaultExpanded={accordionExpanded}>
        <AccordionSummary expandIcon={<RiArrowDownSFill />}>
          <Typography>
            <FormattedMessage id="open_amount" />
          </Typography>

          {openAmountCount > 0 && <InlineBadge label={openAmountCount} />}
        </AccordionSummary>

        <AccordionDetails>
          <Box className={classes.rangeInput}>
            <Field
              id="minOpenAmount"
              name="minOpenAmount"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'min_amount' })}
              size="medium"
            />

            <Box className={classes.dash} />

            <Field
              id="maxOpenAmount"
              name="maxOpenAmount"
              component={AmountTextField}
              label={intl.formatMessage({ id: 'max_amount' })}
              size="medium"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
